import React from 'react'
import { useTranslation } from 'react-i18next'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import './userMenu.css'

type UserMenuItem = {
  key: string
  label: string
  icon: React.ReactNode
  onClick: () => void
  show?: boolean
  danger?: boolean
}

type UserMenuProps = {
  open: boolean
  onOpen: () => void
  onClose: () => void
  displayName: string
  primaryRole: string
  initials: string
  items: UserMenuItem[]
  onLogout: () => void
}

const UserMenu: React.FC<UserMenuProps> = ({
  open,
  onOpen,
  onClose,
  displayName,
  primaryRole,
  initials,
  items,
  onLogout,
}) => {
  const { t } = useTranslation()
  const visible = items.filter((item) => item.show !== false)

  return (
    <div className="incyia-user">
      <button
        type="button"
        className={`incyia-user-trigger${open ? ' is-open' : ''}`}
        data-tour-id="topbar-user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={open ? onClose : onOpen}
      >
        <span className="incyia-user-avatar" aria-hidden>
          {initials}
        </span>
        <span className="incyia-user-meta">
          <span className="incyia-user-name">{displayName}</span>
          <span className="incyia-user-role">{primaryRole}</span>
        </span>
        <KeyboardArrowDownRoundedIcon
          className="incyia-user-caret"
          sx={{ fontSize: 18 }}
        />
      </button>

      {open ? (
        <div className="incyia-user-menu" role="menu">
          <div className="incyia-user-head">
            <span className="incyia-user-avatar incyia-user-avatar--lg" aria-hidden>
              {initials}
            </span>
            <div className="incyia-user-head-copy">
              <div className="incyia-user-head-name">{displayName}</div>
              <div className="incyia-user-head-role">{primaryRole}</div>
            </div>
          </div>

          <div className="incyia-user-section">
            {visible.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className="incyia-user-item"
                onClick={() => {
                  onClose()
                  item.onClick()
                }}
              >
                <span className="incyia-user-item-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="incyia-user-foot">
            <button
              type="button"
              role="menuitem"
              className="incyia-user-item is-danger"
              onClick={onLogout}
            >
              <span className="incyia-user-item-icon">
                <LogoutRoundedIcon />
              </span>
              <span>{t('logout.button')}</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export const userMenuIcons = {
  profile: <PersonOutlineRoundedIcon />,
  settings: <SettingsOutlinedIcon />,
  team: <GroupOutlinedIcon />,
  license: <WorkspacePremiumOutlinedIcon />,
  tour: <ExploreOutlinedIcon />,
}

export default UserMenu
